import { forwardRef, ElementType, HTMLAttributes } from 'react';
import './Text.css';

export type TextSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold';
export type TextColor = 'primary' | 'secondary' | 'muted' | 'error' | 'success' | 'inherit';
export type TextAlign = 'left' | 'center' | 'right' | 'justify';
/**
 * Named text styles. Each maps to a size/weight/colour combination:
 * body (md, regular, primary) · body-sm (sm, regular, primary) ·
 * body-lg (lg, regular, relaxed, primary) · caption (xs, regular, secondary) ·
 * label (sm, medium, tight, primary) · muted (sm, regular, muted).
 * Explicit size/weight/color props override the variant.
 */
export type TextVariant = 'body' | 'body-sm' | 'body-lg' | 'caption' | 'label' | 'muted';

export interface TextProps extends HTMLAttributes<HTMLElement> {
  /** The HTML element to render as. Defaults to 'p'. */
  as?: ElementType;
  /** Named text style; explicit size/weight/color props take precedence. */
  variant?: TextVariant;
  /** Font size variant mapped to a design token. */
  size?: TextSize;
  /** Font weight variant mapped to a design token. */
  weight?: TextWeight;
  /** Color variant mapped to a design token. */
  color?: TextColor;
  /** Text alignment variant. */
  align?: TextAlign;
  /** Additional CSS class names. */
  className?: string;
}

/**
 * Text atom for body and paragraph content.
 * All visual styling is driven exclusively by CSS custom properties
 * defined in Text.css. No hardcoded color or spacing values.
 *
 * CSS custom properties consumed:
 *   --font-size-xs, --font-size-sm, --font-size-base, --font-size-lg, --font-size-xl
 *   --font-weight-regular, --font-weight-medium,
 *   --font-weight-semibold, --font-weight-bold
 *   --color-text-primary, --color-text-secondary,
 *   --color-text-muted, --color-text-error, --color-text-success
 */
export const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      as: Component = 'p',
      variant,
      size,
      weight,
      color,
      align,
      className = '',
      children,
      ...rest
    },
    ref,
  ) => {
    const classes = [
      'text',
      variant ? `text--variant-${variant}` : '',
      size ? `text--${size}` : '',
      weight ? `text--${weight}` : '',
      color ? `text--${color}` : '',
      align ? `text--align-${align}` : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Component ref={ref as unknown as never} className={classes} {...rest}>
        {children}
      </Component>
    );
  },
);

Text.displayName = 'Text';
